import React, { useContext, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import { toast } from "react-toastify";
import api from "../../api";
import EmptyState from "../../components/common/EmptyState";
import { AuthContext } from "../../context/AuthContext";
import { formatDateOrDash } from "./projectWorkspaceUtils";
import {
  DEPENDENCY_CONFLICT_LABELS,
  EMPTY_PLAN_FILTERS,
  LINK_BASIS_LABELS,
  PLAN_STATUS_META,
  SCREEN_STATUS_META,
  VERIFICATION_META,
  allScreens,
  barGeometry,
  countScreens,
  datePercent,
  filterFinalPlan,
  metaFor,
  monthTicks,
  planFilterOptions,
  planFiltersApplied,
} from "./finalPlanUtils";
import "./ProjectWorkspace.css";

// src/pages/admins/ProjectWorkspaceFinalPlan.jsx
//
// Phase 90. Final-plan views (M1-M8 -> Module -> Screen) for the Overview,
// Process, Timeline and Milestones tabs. Every view renders the SAME
// GET /projects/:id/final-plan payload, fetched once by
// ProjectWorkspacePage.jsx, so the four tabs can never disagree on a count.
// Historical D01-D11 views stay available through <PlanViewSwitch>.

const shortDate = (d) => (d ? dayjs(d).format("DD MMM") : "—");

function Pill({ meta, title }) {
  return (
    <span className={`pw-fp-pill pw-fp-pill-${meta.tone}`} title={title || meta.title}>
      {meta.icon && <span aria-hidden="true">{meta.icon}</span>}
      {meta.label}
    </span>
  );
}

function Bar({ value, tone = "primary" }) {
  const pct = value === null || value === undefined ? 0 : Math.max(0, Math.min(100, value));
  return (
    <div className="pw-fp-bar" role="progressbar" aria-valuenow={pct} aria-valuemin={0} aria-valuemax={100}>
      <span className={`pw-fp-bar-fill pw-fp-bar-${tone}`} style={{ width: `${pct}%` }} />
    </div>
  );
}

function Chevron({ open }) {
  return <i className={`fa-solid fa-chevron-${open ? "down" : "right"} pw-fp-chevron`} aria-hidden="true" />;
}

function useToggleSet() {
  const [open, setOpen] = useState(() => new Set());
  const toggle = (key) => setOpen((prev) => {
    const next = new Set(prev);
    if (next.has(key)) next.delete(key); else next.add(key);
    return next;
  });
  return [open, toggle, setOpen];
}

function useTicketNavigation() {
  const navigate = useNavigate();
  const { user } = useContext(AuthContext) || {};
  const basePath = `/${user?.role || "admin"}`;
  return (taskId) => navigate(`${basePath}/tasks/${taskId}`);
}

// Compact counts shown on milestone and module rows.
function CountChips({ counts }) {
  return (
    <div className="pw-fp-counts">
      <span title="Completed / screens (excluding cancelled)"><strong>{counts.completed}</strong>/{counts.eligible} done</span>
      <span title="Pending, in progress or blocked">{counts.pending + counts.in_progress + counts.blocked} open</span>
      {counts.not_linked > 0 && <span className="pw-fp-count-warn" title="Excel rows with no linked ticket">{counts.not_linked} no ticket</span>}
      {counts.verification_pending > 0 && <span className="pw-fp-count-warn" title="Excel says Completed / To Verify; not yet backed by a completed ticket">{counts.verification_pending} to verify</span>}
      {counts.excel_mismatch > 0 && <span className="pw-fp-count-danger" title="Excel says Completed but the ticket is not">{counts.excel_mismatch} mismatch</span>}
      {counts.dependency_conflicts > 0 && <span className="pw-fp-count-danger" title="Planned before an open prerequisite finishes">⚠ {counts.dependency_conflicts}</span>}
    </div>
  );
}

// ---------------------------------------------------------------------------
// View switch: final plan (default) vs. historical D01-D11 phases.
// ---------------------------------------------------------------------------
export function PlanViewSwitch({ view, onChange, plan }) {
  const summary = plan?.summary;
  return (
    <div className="pw-fp-switch" role="group" aria-label="Planning view">
      <button
        type="button"
        className={view === "final" ? "is-active" : ""}
        aria-pressed={view === "final"}
        onClick={() => onChange("final")}
      >
        Final plan · M1–M8
      </button>
      <button
        type="button"
        className={view === "historical" ? "is-active" : ""}
        aria-pressed={view === "historical"}
        onClick={() => onChange("historical")}
      >
        Historical phases · D01–D11
      </button>
      {view === "final" && summary && (
        <span className="pw-fp-switch-note">
          {summary.accounting.excel_rows} Excel planning rows · {summary.tickets.total} unique linked tickets
        </span>
      )}
      {view === "historical" && (
        <span className="pw-fp-switch-note">Historical phase records, kept unchanged for audit</span>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Screen table (shared by Process and Milestones).
// ---------------------------------------------------------------------------
export function ScreenTable({ screens }) {
  const openTicket = useTicketNavigation();
  if (!screens.length) return <p className="text-muted small mb-0 px-2 py-2">No screens match the current filters.</p>;
  return (
    <div className="pw-fp-table-wrap">
      <table className="pw-fp-table">
        <thead>
          <tr>
            <th>Screen / work item</th>
            <th>Type</th>
            <th>Assigned</th>
            <th>Planned</th>
            <th>Ticket</th>
            <th>Excel</th>
            <th>Dependency</th>
          </tr>
        </thead>
        <tbody>
          {screens.map((s) => {
            const status = metaFor(SCREEN_STATUS_META, s.status);
            const verification = s.verification ? VERIFICATION_META[s.verification] : null;
            const dep = s.dependency;
            return (
              <tr key={s.id} className={dep?.conflict ? "pw-fp-row-conflict" : undefined}>
                <td>
                  <div className="pw-fp-screen-name">{s.screen_name}</div>
                  <div className="pw-fp-sub">Row {s.source_order}{s.historical ? " · historical" : ""}</div>
                </td>
                <td><span className="pw-fp-type">{s.work_type || "—"}</span></td>
                <td>
                  <div>{s.assignee_name || "—"}</div>
                  {s.owner_mismatch && (
                    <div className="pw-fp-sub pw-fp-warn-text" title={`Ownership rule (${s.planned_owner.basis}) assigns this to ${s.planned_owner.name}. Not changed automatically.`}>
                      Rule: {s.planned_owner.name}
                    </div>
                  )}
                </td>
                <td className="pw-fp-nowrap">{shortDate(s.planned_start)} – {shortDate(s.planned_end)}</td>
                <td>
                  {s.ticket ? (
                    <>
                      <button
                        type="button"
                        className="pw-fp-ticket-link"
                        onClick={() => openTicket(s.ticket.id)}
                        title={`${s.ticket.title}${s.link_basis ? ` — linked by ${LINK_BASIS_LABELS[s.link_basis] || s.link_basis}` : ""}`}
                      >
                        #{s.ticket.id}
                      </button>{" "}
                      <Pill meta={{ ...status, label: s.ticket.status_label || status.label }} />
                      {s.ticket.overdue && <div className="pw-fp-sub pw-fp-danger-text">Overdue · due {shortDate(s.ticket.due_date)}</div>}
                      {s.ticket.tat_paused && <div className="pw-fp-sub">TAT paused</div>}
                    </>
                  ) : (
                    <>
                      <Pill meta={status} title="No ticket is linked to this Excel row; it is never counted as completed." />
                      {s.excel_ticket_missing && <div className="pw-fp-sub pw-fp-warn-text">Excel ID #{s.excel_ticket_id} not in project</div>}
                    </>
                  )}
                </td>
                <td>
                  <div className="pw-fp-sub-strong">{s.excel_status || "—"}</div>
                  {verification && <Pill meta={verification} />}
                </td>
                <td className="pw-fp-dep">
                  {s.depends_on ? (
                    <>
                      <div className="pw-fp-dep-text" title={s.depends_on}>{s.depends_on}</div>
                      {dep?.conflict ? (
                        <div className="pw-fp-sub pw-fp-danger-text" title={(dep.refs || []).map((r) => `Row ${r.source_order}: ${r.screen_name}`).join("\n")}>
                          ⚠ {DEPENDENCY_CONFLICT_LABELS[dep.conflict]} ({shortDate(dep.open_until)})
                        </div>
                      ) : dep?.kind === "UNRESOLVED" ? (
                        <div className="pw-fp-sub">Not matched to a plan row</div>
                      ) : dep ? (
                        <div className="pw-fp-sub">{dep.open_count ? `${dep.open_count} of ${dep.resolved_count} open` : "Prerequisites done"}</div>
                      ) : null}
                    </>
                  ) : "—"}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function ModuleRows({ milestone, openSet, toggle, forceOpen }) {
  return (
    <div className="pw-fp-modules">
      {milestone.modules.map((mod) => {
        const key = `mod:${milestone.id}:${mod.name}`;
        const open = forceOpen || openSet.has(key);
        const meta = metaFor(PLAN_STATUS_META, mod.status);
        return (
          <div key={key} className="pw-fp-module">
            <button type="button" className="pw-fp-module-head" onClick={() => toggle(key)} aria-expanded={open}>
              <Chevron open={open} />
              <span className="pw-fp-module-name">{mod.name}</span>
              <span className="pw-fp-dates">{shortDate(mod.execution_start)} – {shortDate(mod.planned_end)}</span>
              <span className="pw-fp-bar-cell"><Bar value={mod.progress_percent} tone={meta.tone} /></span>
              <span className="pw-fp-pct">{mod.progress_percent ?? 0}%</span>
              <CountChips counts={mod.counts} />
              <Pill meta={meta} />
            </button>
            {open && <ScreenTable screens={mod.screens} />}
          </div>
        );
      })}
    </div>
  );
}

function MilestoneHead({ m, open, onToggle, action }) {
  const meta = metaFor(PLAN_STATUS_META, m.status);
  return (
    <div className="pw-fp-ms-head">
      <button type="button" className="pw-fp-ms-toggle" onClick={onToggle} aria-expanded={open}>
        <Chevron open={open} />
        <span className="pw-fp-code">{m.code}</span>
        <span className="pw-fp-ms-name">
          {m.name.replace(/^M\d+\s*-\s*/, "")}
          <span className="pw-fp-sub d-block">
            Window {formatDateOrDash(m.execution_start)} – {formatDateOrDash(m.planned_end)}
            {m.actual_date ? ` · completed ${formatDateOrDash(m.actual_date)}` : ""}
            {m.status === "DELAYED" && m.delay_days ? ` · ${m.delay_days}d past plan` : ""}
            {` · ${m.modules.length} modules`}
          </span>
        </span>
        <span className="pw-fp-bar-cell"><Bar value={m.progress_percent} tone={meta.tone} /></span>
        <span className="pw-fp-pct">{m.progress_percent ?? 0}%</span>
        <CountChips counts={m.counts} />
        <Pill meta={meta} />
      </button>
      {action}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Overview tab.
// ---------------------------------------------------------------------------
export function FinalPlanOverview({ plan, overview }) {
  const s = plan.summary;
  const allTickets = overview?.ticketBreakdown?.total;
  const modulesDone = plan.milestones.reduce((n, m) => n + m.modules.filter((x) => x.status === "COMPLETED").length, 0);
  const current = s.current_milestone;
  const next = s.next_milestone;
  const acc = s.accounting;
  const historicalReady = overview?.readyToCompleteMilestones || [];

  return (
    <div className="pw-overview-grid">
      <div>
        <div className="pw-card">
          <h6>Final plan progress</h6>
          <div className="pw-fp-hero">
            <div>
              <div className="pw-fp-hero-pct">{s.screens.progress_percent ?? 0}%</div>
              <div className="pw-fp-sub">of {s.screens.eligible} screens / work items completed by their linked ticket</div>
            </div>
            <div className="flex-fill"><Bar value={s.screens.progress_percent} /></div>
          </div>
          <div className="pw-stat-row mt-3">
            <div className="pw-stat-tile"><strong>{s.screens.completed}</strong><span>Screens done</span></div>
            <div className="pw-stat-tile"><strong>{s.screens.pending + s.screens.in_progress + s.screens.blocked}</strong><span>Screens open</span></div>
            <div className="pw-stat-tile"><strong>{s.screens.not_linked}</strong><span>No ticket yet</span></div>
            <div className="pw-stat-tile"><strong>{s.screens.verification_pending}</strong><span>To verify</span></div>
          </div>
          <div className="pw-fp-sub mt-2">
            Modules completed: <strong>{modulesDone}</strong> of {s.module_count} · Excel says Completed on {s.excel_status_counts.Completed || 0} rows and To Verify on {s.excel_status_counts["To Verify"] || 0}; only linked completed tickets count as done.
          </div>
        </div>

        <div className="pw-card">
          <h6>Linked tickets <span className="pw-fp-sub">(unique tickets — duplicate and unlinked Excel rows are not counted)</span></h6>
          <div className="pw-stat-row">
            <div className="pw-stat-tile"><strong>{s.tickets.total}</strong><span>Linked tickets</span></div>
            <div className="pw-stat-tile"><strong>{s.tickets.completed}</strong><span>Completed</span></div>
            <div className="pw-stat-tile"><strong>{s.tickets.in_progress}</strong><span>In progress</span></div>
            <div className="pw-stat-tile"><strong>{s.tickets.pending + s.tickets.blocked}</strong><span>Pending</span></div>
            <div className="pw-stat-tile"><strong className={s.tickets.overdue ? "text-danger" : undefined}>{s.tickets.overdue}</strong><span>Overdue</span></div>
          </div>
          {allTickets !== undefined && (
            <div className="pw-fp-sub mt-2">
              All project tickets: <strong>{allTickets}</strong> ({Math.max(allTickets - s.tickets.total, 0)} are not linked to any final-plan row).
            </div>
          )}
        </div>

        <div className="pw-card">
          <h6>Milestones M1–M8</h6>
          <div className="pw-fp-ms-mini">
            {plan.milestones.map((m) => {
              const meta = metaFor(PLAN_STATUS_META, m.status);
              return (
                <div key={m.id} className="pw-fp-ms-mini-row">
                  <span className="pw-fp-code">{m.code}</span>
                  <span className="pw-fp-ms-mini-name" title={m.name}>{m.name.replace(/^M\d+\s*-\s*/, "")}</span>
                  <span className="pw-fp-bar-cell"><Bar value={m.progress_percent} tone={meta.tone} /></span>
                  <span className="pw-fp-pct">{m.counts.completed}/{m.counts.eligible}</span>
                  <Pill meta={meta} />
                </div>
              );
            })}
          </div>
        </div>
      </div>

      <div>
        <div className="pw-card">
          <h6>Schedule</h6>
          <div className="d-flex flex-column gap-2" style={{ fontSize: 13 }}>
            <div className="d-flex justify-content-between"><span className="text-muted">Current milestone</span><strong className="text-end">{current ? `${current.code} · ${current.status_label}` : "—"}</strong></div>
            <div className="d-flex justify-content-between"><span className="text-muted">Next incomplete</span><strong className="text-end">{next ? `${next.code} · from ${formatDateOrDash(next.execution_start)}` : "—"}</strong></div>
            <div className="d-flex justify-content-between"><span className="text-muted">Active milestones</span><strong>{s.active_milestone_count}</strong></div>
            <div className="d-flex justify-content-between"><span className="text-muted">Ready to complete</span><strong>{s.ready_to_complete_count}</strong></div>
            <div className="d-flex justify-content-between"><span className="text-muted">Dependency conflicts</span><strong className={s.screens.dependency_conflicts ? "text-danger" : undefined}>{s.screens.dependency_conflicts}</strong></div>
            <div className="d-flex justify-content-between"><span className="text-muted">Project start</span><strong>{formatDateOrDash(plan.projectStartDate)}</strong></div>
            <div className="d-flex justify-content-between"><span className="text-muted">Deadline</span><strong>{formatDateOrDash(plan.projectEndDate)}</strong></div>
          </div>
          {current && (
            <div className="pw-fp-current mt-3">
              <div className="pw-fp-sub">Current</div>
              <strong>{current.name}</strong>
              <div className="pw-fp-sub">Window {formatDateOrDash(current.execution_start)} – {formatDateOrDash(current.planned_end)} · {current.progress_percent ?? 0}% done</div>
            </div>
          )}
        </div>

        <div className="pw-card">
          <h6>Excel rows accounted for</h6>
          <div className="d-flex flex-column gap-1" style={{ fontSize: 12.5 }}>
            <div className="d-flex justify-content-between"><span className="text-muted">Excel planning rows</span><strong>{acc.excel_rows}</strong></div>
            <div className="d-flex justify-content-between"><span className="text-muted">Under an active milestone</span><strong>{acc.rows_in_active_milestones}</strong></div>
            <div className="d-flex justify-content-between"><span className="text-muted">Linked to a ticket</span><strong>{acc.linked_rows}</strong></div>
            <div className="d-flex justify-content-between"><span className="text-muted">Not linked yet</span><strong>{acc.unlinked_rows}</strong></div>
            <div className="d-flex justify-content-between"><span className="text-muted">Alias rows (shared Ticket ID)</span><strong>{acc.alias_rows}</strong></div>
            <div className="d-flex justify-content-between"><span className="text-muted">Repeated screen rows</span><strong>{acc.repeated_screen_rows}</strong></div>
            <div className="d-flex justify-content-between"><span className="text-muted">Placeholder / role not stated</span><strong>{acc.placeholder_rows}</strong></div>
            {acc.excel_ticket_id_not_in_project > 0 && (
              <div className="d-flex justify-content-between text-danger"><span>Excel ID not in project</span><strong>{acc.excel_ticket_id_not_in_project}</strong></div>
            )}
            {acc.rows_without_milestone > 0 && (
              <div className="d-flex justify-content-between text-danger"><span>Rows without an active milestone</span><strong>{acc.rows_without_milestone}</strong></div>
            )}
          </div>
        </div>

        {historicalReady.length > 0 && (
          <div className="pw-card">
            <h6>Historical milestones ready to complete</h6>
            <p className="pw-fp-sub mb-2">All linked work is done; confirm them from the Milestones tab (Historical view).</p>
            {historicalReady.map((m) => (
              <div key={m.id} className="d-flex justify-content-between" style={{ fontSize: 12.5 }}>
                <span>{m.name}</span><span className="text-muted">{formatDateOrDash(m.planned_date)}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Process tab.
// ---------------------------------------------------------------------------
export function FinalPlanProcess({ plan }) {
  const [openSet, toggle] = useToggleSet();
  return (
    <div className="pw-card pw-fp-card">
      <div className="pw-fp-card-head">
        <h6 className="mb-0">Final plan process</h6>
        <span className="pw-fp-sub">Status comes from linked tickets and each milestone's execution window — never from the Excel status alone.</span>
      </div>
      <div className="pw-fp-ms-list">
        {plan.milestones.map((m) => {
          const key = `ms:${m.id}`;
          const open = openSet.has(key);
          return (
            <div key={m.id} className={`pw-fp-ms pw-fp-ms-${metaFor(PLAN_STATUS_META, m.status).tone}`}>
              <MilestoneHead m={m} open={open} onToggle={() => toggle(key)} />
              {open && <ModuleRows milestone={m} openSet={openSet} toggle={toggle} />}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Milestones tab.
// ---------------------------------------------------------------------------
export function FinalPlanMilestones({ plan, projectId, onChanged }) {
  const [filters, setFilters] = useState(EMPTY_PLAN_FILTERS);
  const [openSet, toggle] = useToggleSet();
  const [confirming, setConfirming] = useState(null);
  const setFilter = (k, v) => setFilters((prev) => ({ ...prev, [k]: v }));
  const options = useMemo(() => planFilterOptions(plan.milestones), [plan.milestones]);
  const shown = useMemo(() => filterFinalPlan(plan.milestones, filters), [plan.milestones, filters]);
  const applied = planFiltersApplied(filters);
  const total = countScreens(plan.milestones);
  const matched = countScreens(shown);

  // Explicit sign-off of a READY_TO_COMPLETE milestone: records today as its
  // actual date through the normal milestone update (same permission and
  // validation as the Edit form). Never automatic.
  const confirmComplete = async (m) => {
    setConfirming(m.id);
    try {
      await api.put(`/projects/${projectId}/milestones/${m.id}`, { actual_date: dayjs().format("YYYY-MM-DD") });
      toast.success(`${m.code} marked complete`);
      onChanged?.();
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to update milestone");
    } finally {
      setConfirming(null);
    }
  };

  return (
    <div className="pw-card pw-fp-card">
      <div className="pw-fp-toolbar">
        <div className="pw-fp-search">
          <i className="fa-solid fa-magnifying-glass" aria-hidden="true" />
          <input
            type="search"
            placeholder="Search milestone, module, screen, owner, status or #ticket"
            value={filters.search}
            onChange={(e) => setFilter("search", e.target.value)}
            aria-label="Search final plan"
          />
        </div>
        <select value={filters.milestoneId} onChange={(e) => setFilter("milestoneId", e.target.value)} aria-label="Milestone">
          <option value="">All milestones</option>
          {plan.milestones.map((m) => <option key={m.id} value={m.id}>{m.code} · {m.name.replace(/^M\d+\s*-\s*/, "")}</option>)}
        </select>
        <select value={filters.status} onChange={(e) => setFilter("status", e.target.value)} aria-label="Screen status">
          <option value="">Any status</option>
          {Object.entries(SCREEN_STATUS_META).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
        </select>
        <select value={filters.workType} onChange={(e) => setFilter("workType", e.target.value)} aria-label="Work type">
          <option value="">Any type</option>
          {options.workTypes.map((w) => <option key={w} value={w}>{w}</option>)}
        </select>
        <select value={filters.owner} onChange={(e) => setFilter("owner", e.target.value)} aria-label="Owner">
          <option value="">Any owner</option>
          {options.owners.map((o) => <option key={o} value={o}>{o}</option>)}
        </select>
        <select value={filters.verification} onChange={(e) => setFilter("verification", e.target.value)} aria-label="Excel verification">
          <option value="">Any verification</option>
          {Object.entries(VERIFICATION_META).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
        </select>
        <label className="pw-fp-check">
          <input type="checkbox" checked={filters.conflictsOnly} onChange={(e) => setFilter("conflictsOnly", e.target.checked)} />
          Conflicts only
        </label>
        {applied && (
          <button type="button" className="unit-btn unit-btn-light" onClick={() => setFilters(EMPTY_PLAN_FILTERS)}>Clear</button>
        )}
        <span className="pw-fp-sub ms-auto">{applied ? `${matched} of ${total} screens` : `${total} screens · ${plan.summary.module_count} modules`}</span>
      </div>

      {shown.length === 0 ? (
        <EmptyState title="No matching screens" message="Change or clear the filters to see more of the plan." />
      ) : (
        <div className="pw-fp-ms-list">
          {shown.map((m) => {
            const key = `ms:${m.id}`;
            const open = applied || openSet.has(key);
            return (
              <div key={m.id} className={`pw-fp-ms pw-fp-ms-${metaFor(PLAN_STATUS_META, m.status).tone}`}>
                <MilestoneHead
                  m={m}
                  open={open}
                  onToggle={() => toggle(key)}
                  action={m.status === "READY_TO_COMPLETE" ? (
                    <button
                      type="button"
                      className="unit-btn unit-btn-primary pw-fp-confirm"
                      disabled={confirming === m.id}
                      onClick={() => confirmComplete(m)}
                      title="All screens are completed. Record today as the milestone's actual completion date."
                    >
                      {confirming === m.id ? "Saving…" : "Confirm complete"}
                    </button>
                  ) : null}
                />
                {open && <ModuleRows milestone={m} openSet={openSet} toggle={toggle} forceOpen={applied} />}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Timeline tab.
// ---------------------------------------------------------------------------
const SCREEN_BAR_TONE = { COMPLETED: "success", IN_PROGRESS: "info", BLOCKED: "dark", CANCELLED: "light", PENDING: "secondary", NOT_LINKED: "warning" };

function TimelineRow({ level, label, sub, bar, faint, tone, marker, onClick, open, range }) {
  const geo = bar ? barGeometry(bar[0], bar[1], range[0], range[1]) : null;
  const faintGeo = faint ? barGeometry(faint[0], faint[1], range[0], range[1]) : null;
  const Label = onClick ? "button" : "div";
  return (
    <div className={`pw-fp-tl-row pw-fp-tl-level-${level}`}>
      <Label
        type={onClick ? "button" : undefined}
        className="pw-fp-tl-label"
        onClick={onClick}
        aria-expanded={onClick ? open : undefined}
        title={typeof label === "string" ? label : undefined}
      >
        {onClick && <Chevron open={open} />}
        <span className="pw-fp-tl-label-text">{label}</span>
        {sub && <span className="pw-fp-tl-sub">{sub}</span>}
      </Label>
      <div className="pw-fp-tl-track">
        {faintGeo && <span className="pw-fp-tl-faint" style={{ left: `${faintGeo.left}%`, width: `${faintGeo.width}%` }} />}
        {geo && (
          <span
            className={`pw-fp-tl-bar pw-fp-tl-bar-${tone}`}
            style={{ left: `${geo.left}%`, width: `${geo.width}%` }}
            title={`${shortDate(bar[0])} – ${shortDate(bar[1])}`}
          />
        )}
        {marker && geo && <span className="pw-fp-tl-marker" style={{ left: `${geo.left + geo.width}%` }} title={marker}>⚠</span>}
      </div>
    </div>
  );
}

export function FinalPlanTimeline({ plan }) {
  const [openSet, toggle] = useToggleSet();
  const [conflictsOnly, setConflictsOnly] = useState(false);
  const range = [plan.projectStartDate || plan.planStartDate, plan.projectEndDate || plan.planEndDate];
  const ticks = monthTicks(range[0], range[1]);
  const todayPct = datePercent(plan.today, range[0], range[1]);
  const shown = conflictsOnly ? filterFinalPlan(plan.milestones, { ...EMPTY_PLAN_FILTERS, conflictsOnly: true }) : plan.milestones;
  const conflictCount = allScreens(plan.milestones).filter((s) => s.dependency?.conflict).length;

  return (
    <div className="pw-card pw-fp-card">
      <div className="pw-fp-toolbar">
        <h6 className="mb-0 me-2">Final plan schedule</h6>
        <span className="pw-fp-sub">
          {formatDateOrDash(range[0])} → {formatDateOrDash(range[1])} · bars show each milestone's execution window; the faint band includes historical rows
        </span>
        <label className="pw-fp-check ms-auto">
          <input type="checkbox" checked={conflictsOnly} onChange={(e) => setConflictsOnly(e.target.checked)} />
          Dependency conflicts only ({conflictCount})
        </label>
      </div>

      <div className="pw-fp-tl">
        <div className="pw-fp-tl-row pw-fp-tl-axis">
          <div className="pw-fp-tl-label" />
          <div className="pw-fp-tl-track">
            {ticks.map((t) => (
              <span key={t} className="pw-fp-tl-tick" style={{ left: `${datePercent(t, range[0], range[1])}%` }}>
                {dayjs(t).format("MMM YYYY")}
              </span>
            ))}
          </div>
        </div>
        <div className="pw-fp-tl-body">
          {todayPct !== null && (
            <div className="pw-fp-tl-today-layer" aria-hidden="true">
              <div className="pw-fp-tl-label" />
              <div className="pw-fp-tl-track"><span className="pw-fp-tl-today" style={{ left: `${todayPct}%` }} title={`Today ${formatDateOrDash(plan.today)}`} /></div>
            </div>
          )}
          {shown.length === 0 && <p className="text-muted small p-3 mb-0">No dependency conflicts.</p>}
          {shown.map((m) => {
            const key = `tl:${m.id}`;
            const open = conflictsOnly || openSet.has(key);
            const meta = metaFor(PLAN_STATUS_META, m.status);
            return (
              <React.Fragment key={m.id}>
                <TimelineRow
                  level={0}
                  range={range}
                  label={`${m.code} · ${m.name.replace(/^M\d+\s*-\s*/, "")}`}
                  sub={`${meta.label} · ${m.progress_percent ?? 0}%`}
                  bar={[m.execution_start, m.planned_end]}
                  faint={m.planned_start < m.execution_start ? [m.planned_start, m.last_item_end || m.planned_end] : null}
                  tone={meta.tone}
                  onClick={() => toggle(key)}
                  open={open}
                />
                {open && m.modules.map((mod) => {
                  const modKey = `tl:${m.id}:${mod.name}`;
                  const modOpen = conflictsOnly || openSet.has(modKey);
                  return (
                    <React.Fragment key={modKey}>
                      <TimelineRow
                        level={1}
                        range={range}
                        label={mod.name}
                        sub={`${mod.counts.completed}/${mod.counts.eligible}`}
                        bar={[mod.execution_start, mod.planned_end]}
                        tone={metaFor(PLAN_STATUS_META, mod.status).tone}
                        marker={mod.counts.dependency_conflicts ? `${mod.counts.dependency_conflicts} dependency conflicts` : null}
                        onClick={() => toggle(modKey)}
                        open={modOpen}
                      />
                      {modOpen && mod.screens.map((s) => (
                        <TimelineRow
                          key={s.id}
                          level={2}
                          range={range}
                          label={s.screen_name}
                          sub={s.ticket ? `#${s.ticket.id}` : "no ticket"}
                          bar={[s.planned_start, s.planned_end]}
                          tone={SCREEN_BAR_TONE[s.status] || "secondary"}
                          marker={s.dependency?.conflict ? `${DEPENDENCY_CONFLICT_LABELS[s.dependency.conflict]} (open until ${shortDate(s.dependency.open_until)})` : null}
                        />
                      ))}
                    </React.Fragment>
                  );
                })}
              </React.Fragment>
            );
          })}
        </div>
      </div>
      <div className="pw-fp-legend">
        {["success", "info", "secondary", "warning", "danger"].map((tone) => (
          <span key={tone}><i className={`pw-fp-legend-dot pw-fp-tl-bar-${tone}`} />{
            { success: "Completed", info: "In progress", secondary: "Pending / not started", warning: "No ticket / at risk", danger: "Delayed" }[tone]
          }</span>
        ))}
        <span>⚠ Dependency conflict (dates are flagged, never moved)</span>
      </div>
    </div>
  );
}
